import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Close,
  CreditCard,
  Security,
  CheckCircle,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { LoyaltyProgram } from '../../types';

interface AddCardDialogProps {
  open: boolean;
  onClose: () => void;
  onAddCard: (card: Omit<LoyaltyProgram, 'id'>) => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    maxWidth: 600,
    width: '100%',
    margin: theme.spacing(2),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
}));

const PreviewCard = styled(Card)<{ cardcolor: string }>(({ cardcolor }) => ({
  background: `linear-gradient(135deg, ${cardcolor} 0%, ${cardcolor}dd 100%)`,
  color: 'white',
  borderRadius: 16,
  minHeight: 200,
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '20%',
    right: '-20%',
    width: '150%',
    height: '150%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    zIndex: 1,
  }
}));

const CardChip = styled(Box)(() => ({
  position: 'absolute',
  top: '60px',
  left: '20px',
  width: '30px',
  height: '24px',
  background: 'linear-gradient(135deg, #ffd700 0%, #ffed4a 100%)',
  borderRadius: '4px',
  zIndex: 3,
  border: '1px solid rgba(0,0,0,0.1)',
}));

const steps = ['Card Details', 'Bank Information', 'Review & Add'];

const cardNetworks = [
  { value: 'visa', label: 'Visa', color: '#1a1f71' },
  { value: 'mastercard', label: 'Mastercard', color: '#eb001b' },
  { value: 'amex', label: 'American Express', color: '#006fcf' },
  { value: 'discover', label: 'Discover', color: '#ff6000' },
  { value: 'rupay', label: 'RuPay', color: '#097969' },
  { value: 'diners', label: 'Diners Club', color: '#0079be' },
];

const cardTiers = [
  { value: 'Basic', color: '#6b7280' },
  { value: 'Silver', color: '#9ca3af' },
  { value: 'Gold', color: '#f59e0b' },
  { value: 'Platinum', color: '#6b7280' },
  { value: 'Premium', color: '#7c3aed' },
  { value: 'Black', color: '#1f2937' },
];

const popularCards = [
  // International Cards
  { name: 'Chase Sapphire Reserve', network: 'visa', tier: 'Premium', color: '#1A365D', website: 'https://creditcards.chase.com/rewards-credit-cards/sapphire/reserve', region: 'US' },
  { name: 'American Express Platinum', network: 'amex', tier: 'Platinum', color: '#006FCF', website: 'https://www.americanexpress.com/us/credit-cards/card/platinum/', region: 'US' },
  { name: 'Capital One Venture X', network: 'mastercard', tier: 'Premium', color: '#004879', website: 'https://www.capitalone.com/credit-cards/venture-x/', region: 'US' },
  { name: 'Chase Freedom Unlimited', network: 'visa', tier: 'Basic', color: '#0066b2', website: 'https://creditcards.chase.com/cash-back-credit-cards/freedom/unlimited', region: 'US' },
  { name: 'Citi Double Cash', network: 'mastercard', tier: 'Basic', color: '#0066B2', website: 'https://www.citi.com/credit-cards/citi-double-cash-credit-card', region: 'US' },
  { name: 'Discover it Cash Back', network: 'discover', tier: 'Basic', color: '#FF6000', website: 'https://www.discover.com/credit-cards/cash-back/it-card.html', region: 'US' },
  
  // Indian Cards
  { name: 'HDFC Infinia', network: 'visa', tier: 'Premium', color: '#004C8F', website: 'https://www.hdfcbank.com/personal/pay/cards/credit-cards/infinia', region: 'India' },
  { name: 'HDFC Diners Club Black', network: 'diners', tier: 'Black', color: '#1f2937', website: 'https://www.hdfcbank.com/personal/pay/cards/credit-cards/diners-club-black', region: 'India' },
  { name: 'Axis Magnus', network: 'mastercard', tier: 'Premium', color: '#97144D', website: 'https://www.axisbank.com/retail/cards/credit-card/axis-bank-magnus-credit-card', region: 'India' },
  { name: 'SBI Card Elite', network: 'visa', tier: 'Premium', color: '#1C4E80', website: 'https://www.sbicard.com/en/personal/credit-cards/premium/sbi-card-elite.page', region: 'India' },
  { name: 'ICICI Bank Sapphiro', network: 'visa', tier: 'Premium', color: '#B95600', website: 'https://www.icicibank.com/personal-banking/cards/credit-card/sapphiro-credit-card', region: 'India' },
  { name: 'American Express Platinum Travel', network: 'amex', tier: 'Platinum', color: '#006FCF', website: 'https://www.americanexpress.com/in/credit-cards/platinum-travel-credit-card/', region: 'India' },
  { name: 'Axis Vistara Infinite', network: 'visa', tier: 'Premium', color: '#7B2869', website: 'https://www.axisbank.com/retail/cards/credit-card/co-brand/club-vistara-infinite', region: 'India' },
  { name: 'HDFC Regalia Gold', network: 'visa', tier: 'Gold', color: '#C5A572', website: 'https://www.hdfcbank.com/personal/pay/cards/credit-cards/regalia-gold', region: 'India' },
  { name: 'IndusInd Legend', network: 'visa', tier: 'Premium', color: '#E84E0F', website: 'https://www.indusind.com/in/en/personal/cards/credit-card/indusind-bank-legend-credit-card.html', region: 'India' },
  { name: 'Yes Bank Marquee', network: 'mastercard', tier: 'Premium', color: '#1C3664', website: 'https://www.yesbank.in/personal-banking/yes-individual/cards/credit-cards/yes-marquee', region: 'India' },
  { name: 'IDFC First Wealth', network: 'visa', tier: 'Premium', color: '#C41E3A', website: 'https://www.idfcfirstbank.com/credit-card/premium-cards/wealth-credit-card', region: 'India' },
  { name: 'Kotak Royale Signature', network: 'visa', tier: 'Platinum', color: '#ED1C24', website: 'https://www.kotak.com/en/personal-banking/cards/credit-cards/kotak-royale-signature-credit-card.html', region: 'India' },
];

const AddCardDialog: React.FC<AddCardDialogProps> = ({ open, onClose, onAddCard }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [regionFilter, setRegionFilter] = useState<'All' | 'US' | 'India'>('All');
  const [cardData, setCardData] = useState({
    name: '',
    cardNumber: '',
    cardType: '',
    tier: '',
    color: '#1A365D',
    currency: 'Points',
    points: 0,
    website: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setCardData({
      name: '',
      cardNumber: '',
      cardType: '',
      tier: '',
      color: '#1A365D',
      currency: 'Points',
      points: 0,
      website: '',
    });
    setErrors({});
    onClose();
  };

  const validateStep = () => {
    const newErrors: Record<string, string> = {};
    
    if (activeStep === 0) {
      if (!cardData.name.trim()) newErrors.name = 'Card name is required';
      if (!cardData.cardNumber.trim()) {
        newErrors.cardNumber = 'Card number is required';
      } else if (cardData.cardNumber.length !== 4) {
        newErrors.cardNumber = 'Please enter last 4 digits only';
      }
      if (!cardData.cardType) newErrors.cardType = 'Card network is required';
      if (!cardData.tier) newErrors.tier = 'Card tier is required';
    }
    
    if (activeStep === 1) {
      if (!cardData.currency.trim()) newErrors.currency = 'Currency/Points name is required';
      if (!cardData.website.trim()) newErrors.website = 'Bank website is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setCardData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleQuickAdd = (card: any) => {
    // Pre-populate card details but still require user to enter their own info
    setCardData({
      name: card.name,
      cardNumber: '', // Leave empty for user to enter their actual last 4 digits
      cardType: card.network,
      tier: card.tier,
      color: card.color,
      currency: 'Points',
      points: 0, // Start with 0 points
      website: card.website || '',
    });
    // Stay on step 0 so user can enter their card details
    setActiveStep(0);
  };

  const handleSubmit = () => {
    if (validateStep()) {
      const newCard: Omit<LoyaltyProgram, 'id'> = {
        name: cardData.name,
        type: 'credit_card',
        logo: '', // Will be generated by the system
        color: cardData.color,
        points: cardData.points,
        tier: cardData.tier,
        tierColor: cardData.color,
        currency: cardData.currency,
        conversionRate: 1.0,
        website: cardData.website,
        redemptionUrl: cardData.website,
        cardNumber: cardData.cardNumber,
        cardType: cardData.cardType as 'visa' | 'mastercard' | 'amex' | 'discover',
      };
      
      onAddCard(newCard);
      handleClose();
    }
  };

  const formatCardNumber = (number: string) => {
    if (!number) return '•••• •••• •••• ••••';
    return `•••• •••• •••• ${number}`;
  };

  const getNetworkColor = () => {
    const network = cardNetworks.find(n => n.value === cardData.cardType);
    return network?.color || '#666';
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Card Information
            </Typography>
            
            {/* Quick Add Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Quick Start with Popular Cards
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Click to pre-fill details, then enter your card number and personal information
              </Typography>
              
              {/* Region Filter */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center' }}>
                <Chip 
                  label="All Cards" 
                  onClick={() => setRegionFilter('All')}
                  color={regionFilter === 'All' ? 'primary' : 'default'}
                  sx={{ fontWeight: regionFilter === 'All' ? 600 : 400 }}
                />
                <Chip 
                  label="🇺🇸 US Cards" 
                  onClick={() => setRegionFilter('US')}
                  color={regionFilter === 'US' ? 'primary' : 'default'}
                  sx={{ fontWeight: regionFilter === 'US' ? 600 : 400 }}
                />
                <Chip 
                  label="🇮🇳 Indian Cards" 
                  onClick={() => setRegionFilter('India')}
                  color={regionFilter === 'India' ? 'primary' : 'default'}
                  sx={{ fontWeight: regionFilter === 'India' ? 600 : 400 }}
                />
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                {popularCards
                  .filter(card => regionFilter === 'All' || card.region === regionFilter)
                  .map((card) => (
                  <Box key={card.name}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer', 
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                        transition: 'all 0.2s ease',
                        border: '2px solid',
                        borderColor: cardData.name === card.name ? card.color : 'divider',
                        bgcolor: cardData.name === card.name ? `${card.color}08` : 'background.paper',
                      }}
                      onClick={() => handleQuickAdd(card)}
                    >
                      <CardContent sx={{ p: 2, textAlign: 'center' }}>
                        <CreditCard sx={{ color: card.color, mb: 1 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                          {card.name}
                        </Typography>
                        <Chip 
                          label={card.tier} 
                          size="small" 
                          sx={{ mt: 0.5, fontSize: '10px' }}
                        />
                        {cardData.name === card.name && (
                          <Chip 
                            label="Selected" 
                            size="small" 
                            color="primary"
                            sx={{ mt: 0.5, ml: 0.5, fontSize: '10px' }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
              
              <Typography variant="body2" sx={{ mt: 2, mb: 2, color: 'text.secondary', textAlign: 'center' }}>
                Or start from scratch:
              </Typography>
            </Box>

            {cardData.name && popularCards.some(card => card.name === cardData.name) && (
              <Alert 
                severity="info" 
                sx={{ mb: 2, borderRadius: 2 }}
                icon={<CheckCircle />}
              >
                <Typography variant="body2">
                  <strong>{cardData.name}</strong> details pre-filled! Please enter your personal card information below.
                </Typography>
              </Alert>
            )}

            <StyledTextField
              fullWidth
              label="Card Name"
              value={cardData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 3 }}
              placeholder="e.g., Chase Sapphire Reserve"
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <StyledTextField
                label="Last 4 Digits"
                value={cardData.cardNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                  handleInputChange('cardNumber', value);
                }}
                error={!!errors.cardNumber}
                helperText={errors.cardNumber || 'For security, only enter last 4 digits'}
                type={showCardNumber ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowCardNumber(!showCardNumber)}
                      edge="end"
                      size="small"
                    >
                      {showCardNumber ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
                inputProps={{ maxLength: 4 }}
              />

              <FormControl error={!!errors.cardType} sx={{ minWidth: 150 }}>
                <InputLabel>Network</InputLabel>
                <Select
                  value={cardData.cardType}
                  label="Network"
                  onChange={(e) => handleInputChange('cardType', e.target.value)}
                >
                  {cardNetworks.map((network) => (
                    <MenuItem key={network.value} value={network.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 12,
                            backgroundColor: network.color,
                            borderRadius: 1,
                          }}
                        />
                        {network.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <FormControl fullWidth error={!!errors.tier}>
              <InputLabel>Card Tier</InputLabel>
              <Select
                value={cardData.tier}
                label="Card Tier"
                onChange={(e) => {
                  const tier = e.target.value;
                  const tierData = cardTiers.find(t => t.value === tier);
                  handleInputChange('tier', tier);
                  if (tierData) {
                    handleInputChange('color', tierData.color);
                  }
                }}
              >
                {cardTiers.map((tier) => (
                  <MenuItem key={tier.value} value={tier.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: tier.color,
                          borderRadius: '50%',
                        }}
                      />
                      {tier.value}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Bank & Rewards Information
            </Typography>

            <StyledTextField
              fullWidth
              label="Rewards Currency"
              value={cardData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              error={!!errors.currency}
              helperText={errors.currency || 'e.g., Points, Miles, Cashback'}
              sx={{ mb: 3 }}
            />

            <StyledTextField
              fullWidth
              label="Current Points/Miles"
              type="number"
              value={cardData.points || ''}
              onChange={(e) => handleInputChange('points', parseInt(e.target.value) || 0)}
              helperText="Enter your current reward balance"
              sx={{ mb: 3 }}
            />

            <StyledTextField
              fullWidth
              label="Bank Website"
              value={cardData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              error={!!errors.website}
              helperText={errors.website || 'e.g., https://chase.com'}
              placeholder="https://"
            />
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Review Your Card
            </Typography>

            <PreviewCard cardcolor={cardData.color} sx={{ mb: 3 }}>
              <CardContent sx={{ position: 'relative', zIndex: 2, height: 200, p: 3 }}>
                {/* Network Logo */}
                <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 3 }}>
                  <Box sx={{
                    background: 'white',
                    color: getNetworkColor(),
                    px: 1,
                    py: 0.5,
                    borderRadius: '4px',
                    minWidth: '45px',
                    textAlign: 'center',
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '8px' }}>
                      {cardData.cardType.toUpperCase()}
                    </Typography>
                  </Box>
                </Box>

                {/* EMV Chip */}
                <CardChip />

                {/* Card Number */}
                <Typography 
                  sx={{
                    position: 'absolute',
                    bottom: 60,
                    left: 20,
                    fontFamily: 'Courier New, monospace',
                    fontSize: '16px',
                    fontWeight: 600,
                    letterSpacing: '2px',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  {formatCardNumber(cardData.cardNumber)}
                </Typography>

                {/* Card Name */}
                <Typography 
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: 20,
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  {cardData.name}
                </Typography>
              </CardContent>
            </PreviewCard>

            <Card sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Card Summary
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{cardData.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Network</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                      {cardData.cardType}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Tier</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{cardData.tier}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Rewards</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cardData.points.toLocaleString()} {cardData.currency}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Your card will be added to your wallet and you can start tracking rewards immediately.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCard color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Add Credit Card
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
        
        <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
        )}
        
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<CheckCircle />}
            sx={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
              fontWeight: 600,
            }}
          >
            Add Card
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            startIcon={<Security />}
            sx={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
              fontWeight: 600,
            }}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default AddCardDialog;